import { useEffect, useState } from 'react'
import { Activity, RefreshCw, CheckCircle2, XCircle, Clock, Circle, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Run {
  repo: string
  name: string
  branch: string
  event: string
  status: string
  conclusion: string
  verdict: string
  url: string
  started: string
  actor: string
}

const VERDICT: Record<string, { icon: any; tone: string; label: string }> = {
  success:     { icon: CheckCircle2, tone: 'text-emerald',     label: 'success' },
  failure:     { icon: XCircle,      tone: 'text-rose',         label: 'failure' },
  in_progress: { icon: Clock,        tone: 'text-amber-light',  label: 'running' },
  cancelled:   { icon: Circle,       tone: 'text-text-muted',   label: 'cancelled' },
  skipped:     { icon: Circle,       tone: 'text-text-muted',   label: 'skipped' },
  neutral:     { icon: Circle,       tone: 'text-text-muted',   label: 'neutral' },
}

export default function CiStatusPage() {
  const [data, setData] = useState<{ runs: Run[]; counts: any; watched_repos: number; total: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  const fetchAll = () => {
    setLoading(true)
    api.get('/api/devops/ci-status', { params: { per_repo: 3 } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchAll() }, [])

  const list = (data?.runs || []).filter(r => filter === 'all' || r.verdict === filter)

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Activity size={24} className="text-cyan" /> CI Status
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Recent GitHub Actions workflow runs across {data?.watched_repos || 0} watched repos.
          </p>
        </div>
        <button type="button" onClick={fetchAll} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {data?.counts && (
        <div className="flex flex-wrap gap-2">
          {[
            { v: 'all',         label: 'All',         n: data.total },
            { v: 'success',     label: 'Success',     n: data.counts.success || 0 },
            { v: 'failure',     label: 'Failure',     n: data.counts.failure || 0 },
            { v: 'in_progress', label: 'Running',     n: data.counts.in_progress || 0 },
          ].map(c => (
            <button key={c.v} type="button" onClick={() => setFilter(c.v)}
              className={clsx('px-3 py-1.5 rounded-md border text-[11px]',
                filter === c.v ? 'bg-blue/20 text-blue-light border-blue/50' : 'border-border hover:bg-surface-hover')}>
              {c.label} <span className="font-mono ml-1">{c.n}</span>
            </button>
          ))}
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
              <th className="text-left py-2 px-2"></th>
              <th className="text-left py-2 px-2">Repo / workflow</th>
              <th className="text-left py-2 px-2">Branch</th>
              <th className="text-left py-2 px-2">Event</th>
              <th className="text-left py-2 px-2">Started</th>
              <th className="text-right py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-4 text-text-muted text-[11px]">Loading…</td></tr>}
            {!loading && list.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-text-muted text-[11px] italic">No runs match.</td></tr>}
            {list.map((r, i) => {
              const v = VERDICT[r.verdict] || VERDICT.neutral
              const Icon = v.icon
              return (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-1.5 px-2"><Icon size={14} className={v.tone} /></td>
                  <td className="py-1.5 px-2">
                    <span className="font-mono text-blue-light text-[10px]">{r.repo}</span>
                    <div className="text-text-primary text-[11px]">{r.name}</div>
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px]">{r.branch || '—'}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{r.event}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">
                    {r.started && formatDistanceToNow(new Date(r.started), { addSuffix: true })}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <a href={r.url} target="_blank" rel="noreferrer noopener" className="text-blue-light hover:underline">
                      <ExternalLink size={10} />
                    </a>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
